import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthMe } from "../lib/queries.js";
import { useRelayStatus } from "../lib/ws.js";
import { useOddsFormat, ODDS_FORMATS } from "../lib/odds.js";
import { useOrderSettings, setOrderSetting, type OrderSettings } from "../lib/settings.js";
import { useTradingMode, setTradingMode, useVenueStatus, fmtVenueBalance, type VenueState } from "../lib/venues.js";
import { fmtMoney } from "../lib/format.js";
import { showCapabilities } from "./CapabilitiesModal.js";
import { showTour } from "./WelcomeTour.js";
import { VenueLogo } from "./VenueLogo.js";
import { disconnect as omDisconnect, SCOPES, useCanTrade, useIsAuthed } from "../lib/omauth.js";
import { SignInButton } from "./SignInWithOpenMarkets.js";
import { showUnlock } from "./Gate.js";

/**
 * Top bar. Desktop (≥lg): the full live-status cluster (mode, venues, WS dot, rate budget,
 * odds toggle, settings, account chip) — one slim row, unchanged. Mobile (<lg) is a different
 * product: just the menu button, the page title, and the Paper/Real state. Everything else
 * lives in the nav drawer's account/preferences sections (see MobileNav), where it has room
 * to breathe instead of being crushed into a 44px toolbar.
 */
export function TopBar({ title, onMenu }: { title?: string; onMenu?: () => void }) {
  // Signed out: only what works without a session (odds format, the tour) plus Sign in. The
  // account-only cluster (mode, venues/balances, WS, usage meter, order settings, key chip)
  // isn't even mounted, so none of its authenticated queries can fire.
  const authed = useIsAuthed();
  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-panel-2 px-3 sm:px-4 lg:h-11">
      {/* mobile hamburger — opens the nav drawer (desktop rail is hidden <lg) */}
      <button
        onClick={onMenu}
        aria-label="menu"
        className="-ml-1.5 grid h-11 w-11 shrink-0 place-items-center rounded-md text-dim hover:bg-raised hover:text-ink lg:hidden"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {title && <h1 className="min-w-0 break-words text-[15px] font-semibold tracking-tight text-ink lg:text-sm">{title}</h1>}

      {/* mobile (<lg): exactly one control — the Paper/Real spine. Nothing else. */}
      <div className="ml-auto lg:hidden">{authed ? <MobileModeSwitch /> : <SignInButton />}</div>

      {!authed && (
        <div className="ml-auto hidden items-center gap-4 lg:flex">
          <OddsToggle />
          <span className="h-4 w-px bg-border" />
          <button
            onClick={() => showTour()}
            title="Replay the getting-started tour"
            className="grid h-5 w-5 place-items-center rounded-full border border-border-2 text-[11px] text-faint hover:border-up hover:text-up"
          >
            ?
          </button>
          <SignInButton />
        </div>
      )}

      {/* desktop (≥lg): the full status cluster, exactly as before */}
      {authed && (
      <div className="ml-auto hidden items-center gap-4 lg:flex">
        {/* the spine: paper vs real, and which venues you can execute on */}
        <TradingModeSwitch />
        <VenuesChip />
        <span className="h-4 w-px bg-border" />
        <WsDot />
        <span className="hidden lg:block">
          <UsageMeter />
        </span>
        <span className="hidden sm:block">
          <OddsToggle />
        </span>
        <span className="h-4 w-px bg-border" />
        <button
          onClick={() => showTour()}
          title="Replay the getting-started tour"
          className="grid h-5 w-5 place-items-center rounded-full border border-border-2 text-[11px] text-faint hover:border-up hover:text-up"
        >
          ?
        </button>
        <SettingsMenu />
        <AccountChip />
      </div>
      )}
    </header>
  );
}

/**
 * Mobile-only Paper/Real switch — the one control that earns a slot on the phone top bar.
 * Same semantics as the desktop TradingModeSwitch, sized for thumbs (44px targets).
 */
function MobileModeSwitch() {
  const mode = useTradingMode();
  const canTrade = useCanTrade();
  return (
    <div className="flex overflow-hidden rounded-lg border border-border-2 text-xs font-semibold">
      <button
        onClick={() => setTradingMode("paper")}
        className={`min-h-11 px-3.5 ${mode === "paper" ? "bg-blue/20 text-blue" : "text-dim active:bg-raised"}`}
      >
        Paper
      </button>
      <button
        onClick={() => (canTrade ? setTradingMode("real") : showUnlock(SCOPES.trade))}
        title={canTrade ? undefined : "Unlock real-money trading"}
        className={`min-h-11 border-l border-border-2 px-3.5 ${
          mode === "real" ? "bg-down/25 text-down" : canTrade ? "text-dim active:bg-raised" : "text-amber active:bg-raised"
        }`}
      >
        {canTrade ? "Real" : "Real 🔒"}
      </button>
    </div>
  );
}

/**
 * Global trading mode — the spine of the connection story. Paper opens every venue (books the
 * internal book); Real focuses the app on venues you can execute on. Real is unavailable
 * without the trade scope (kept on Paper, with an upgrade hint).
 */
function TradingModeSwitch() {
  const mode = useTradingMode();
  const canTrade = useCanTrade();
  return (
    <div className="flex items-center gap-2">
    <div
      className="flex overflow-hidden rounded-md border border-border-2 text-[11px] font-semibold"
      title={
        canTrade
          ? "Paper books the internal book on any venue; Real routes to your connected, funded venues."
          : "Real-money trading isn’t on your OpenMarkets plan yet — unlock it to trade with real money. Paper works now."
      }
    >
      <button
        onClick={() => setTradingMode("paper")}
        className={`px-2.5 py-1 ${mode === "paper" ? "bg-blue/20 text-blue" : "text-dim hover:bg-raised"}`}
      >
        Paper
      </button>
      <button
        onClick={() => (canTrade ? setTradingMode("real") : showUnlock(SCOPES.trade))}
        className={`px-2.5 py-1 ${
          mode === "real" ? "bg-down/25 text-down" : canTrade ? "text-dim hover:bg-raised" : "text-faint/60 hover:bg-raised"
        }`}
      >
        Real{!canTrade && " 🔒"}
      </button>
    </div>
    {!canTrade && (
      <button
        onClick={() => showUnlock(SCOPES.trade)}
        title="Why real money needs an upgrade"
        className="rounded-md bg-up/20 px-2.5 py-1 text-[11px] font-semibold text-up hover:bg-up/30"
      >
        Unlock real money
      </button>
    )}
    </div>
  );
}

/** Shared venue-state vocabulary (dot color + label) — used here and by VenueLogo's dot. */
export const VENUE_STATE_META: Record<VenueState, { dot: string; text: string; label: string }> = {
  live: { dot: "bg-up", text: "text-up", label: "Live" },
  nofunds: { dot: "bg-amber", text: "text-amber", label: "No funds" },
  off: { dot: "bg-faint", text: "text-faint", label: "Not connected" },
};

/**
 * Global venue-status chip → popover. At-a-glance "how many venues can I actually execute on
 * and how much is behind them", with the full breakdown (state + balance per venue) and a
 * connect CTA. This is the app-wide anchor for the connection story.
 */
function VenuesChip() {
  const vs = useVenueStatus();
  const mode = useTradingMode();
  const paper = mode === "paper";
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const rank: Record<VenueState, number> = { live: 0, nofunds: 1, off: 2 };
  const realSorted = [...vs.realVenues].sort(
    (a, b) => rank[a.state] - rank[b.state] || (b.balance ?? 0) - (a.balance ?? 0),
  );

  return (
    <div className="relative" ref={ref}>
      {/* In Paper mode the relevant balance is your ATLAS play wallet; in Real it's your live
          venues. The chip reflects the current mode. */}
      <button
        onClick={() => setOpen((v) => !v)}
        title={paper ? "Your paper (ATLAS) wallet" : "Connected venues & real balances"}
        className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] hover:bg-raised ${
          paper ? "border-blue/30 text-blue" : "border-border-2 text-dim hover:text-ink"
        }`}
      >
        {paper ? (
          <>
            <span>Paper wallet</span>
            <span className="nums font-semibold">{fmtVenueBalance(vs.paperWallet?.balance ?? 0, "ATLAS")}</span>
          </>
        ) : (
          <>
            <span className={`h-1.5 w-1.5 rounded-full ${vs.liveCount > 0 ? "bg-up" : "bg-faint"}`} />
            <span className="font-semibold text-ink">{vs.liveCount}</span>
            <span>live</span>
            {vs.realBalance > 0 && <span className="nums text-faint">· {fmtMoney(vs.realBalance)}</span>}
          </>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-50 w-80 rounded-md border border-border bg-panel-2 shadow-xl">
          {/* paper wallet — always shown, highlighted when in paper mode */}
          {vs.paperWallet && (
            <div className={`border-b border-border px-3 py-2 ${paper ? "bg-blue/5" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-ink">Paper wallet</span>
                <span className="nums text-[12px] text-blue">{fmtVenueBalance(vs.paperWallet.balance, "ATLAS")}</span>
              </div>
              <div className="text-[10px] text-faint">Play money · the internal book you paper-trade against</div>
            </div>
          )}

          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">Real venues</span>
            <span className="text-[11px] text-dim">
              {vs.liveCount} live · {fmtMoney(vs.realBalance)}
            </span>
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {vs.loading ? (
              <div className="px-3 py-3 text-[11px] text-faint">loading venues…</div>
            ) : realSorted.length === 0 ? (
              <div className="px-3 py-3 text-[11px] text-faint">No real venues connected yet.</div>
            ) : (
              realSorted.map((v) => {
                const m = VENUE_STATE_META[v.state];
                return (
                  <div key={v.partnerId} className="flex items-center gap-2 px-3 py-1.5">
                    <VenueLogo name={v.name} logoUrl={v.logoUrl} size={18} status={v.state} />
                    <span className="min-w-0 flex-1 break-words text-[12px] text-ink">{v.name}</span>
                    <span className={`text-[10px] ${m.text}`}>{m.label}</span>
                    <span className="nums w-16 text-right text-[11px] text-dim">
                      {fmtVenueBalance(v.balance, v.currency)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
          <a
            href="https://app.openmarkets.ai"
            target="_blank"
            rel="noreferrer"
            className="block border-t border-border px-3 py-2 text-center text-[11px] font-semibold text-amber hover:bg-amber/5"
          >
            Connect or fund venues ↗
          </a>
        </div>
      )}
    </div>
  );
}

/** ⚙ order settings — now just the limit-orders gate (real/paper is the trading-mode switch). */
function SettingsMenu() {
  const settings = useOrderSettings();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (key: keyof OrderSettings, value: boolean) => {
    setOrderSetting(key, value);
    qc.invalidateQueries({ queryKey: ["config"] });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[11px] text-faint hover:text-dim"
        title="Order settings"
      >
        <span className="text-sm leading-none">⚙</span>
      </button>
      {open && (
        <div className="absolute right-0 top-6 z-50 w-72 rounded-md border border-border bg-panel-2 p-3 shadow-lg">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-faint">Order settings</div>
          <SettingRow
            label="Enable limit orders"
            hint="Resting limit orders (the venue must also support them). Off → market/IOC only."
            checked={settings.enable_limit_orders}
            onChange={(v) => toggle("enable_limit_orders", v)}
          />
          <p className="mt-2 text-[10px] leading-relaxed text-faint">
            Real vs paper is the Paper/Real switch in the top bar. Stored in this browser only.
          </p>
        </div>
      )}
    </div>
  );
}

function SettingRow({
  label,
  hint,
  checked,
  danger,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  danger?: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className={`flex items-start gap-2 py-1.5 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-current"
      />
      <span className="min-w-0">
        <span className={`text-[12px] font-medium ${danger && checked ? "text-down" : "text-ink"}`}>{label}</span>
        <span className="mt-0.5 block text-[10px] leading-snug text-faint">{hint}</span>
      </span>
    </label>
  );
}

function OddsToggle() {
  const { format, setFormat } = useOddsFormat();
  return (
    <div
      className="flex overflow-hidden rounded border border-border-2"
      title="odds format: cents / implied % / American"
    >
      {ODDS_FORMATS.map((f) => (
        <button
          key={f.value}
          onClick={() => setFormat(f.value)}
          className={`px-2 py-0.5 text-[11px] ${
            format === f.value ? "bg-amber/20 text-amber" : "text-dim hover:bg-raised"
          }`}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}

/** Compact number: 11183 → "11.2k", 50000 → "50k". */
function compact(n: number): string {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}k`;
}

/**
 * API usage meter — daily requests used vs your key's per-day limit, from the `auth/me` body
 * (the live per-minute headers aren't CORS-exposed to the browser). Tooltip carries 24h/7d
 * requests, errors, and credits remaining.
 */
function UsageMeter() {
  const { data } = useAuthMe();
  const used = data?.usage?.requests_last_24h ?? null;
  const dayLimit = data?.current_key?.rate_limit_per_day ?? null;
  if (used == null || dayLimit == null || dayLimit === 0) return null;
  const frac = Math.max(0, Math.min(1, used / dayLimit));
  const pct = Math.round(frac * 100);
  const color = frac < 0.7 ? "bg-blue" : frac < 0.9 ? "bg-amber" : "bg-down";
  const textColor = frac < 0.7 ? "text-dim" : frac < 0.9 ? "text-amber" : "text-down";
  const credits = data?.credits?.total ?? null;
  const errors = data?.usage?.errors_last_24h ?? null;
  const weekly = data?.usage?.requests_last_7d ?? null;
  return (
    <span
      className="flex items-center gap-1.5 text-[11px]"
      title={
        `API requests (24h): ${used.toLocaleString()} / ${dayLimit.toLocaleString()}/day` +
        (weekly != null ? ` · ${weekly.toLocaleString()} in 7d` : "") +
        (errors != null ? ` · ${errors} errors (24h)` : "") +
        (credits != null ? ` · ${credits.toLocaleString()} credits left` : "")
      }
    >
      <span className="text-faint">API</span>
      <span className="relative h-2 w-14 overflow-hidden rounded-sm bg-raised">
        <span className={`absolute inset-y-0 left-0 ${color}`} style={{ width: `${pct}%` }} />
      </span>
      <span className={`nums ${textColor}`}>
        {compact(used)}/{compact(dayLimit)}
      </span>
    </span>
  );
}

function WsDot() {
  const { display, socket, upstream } = useRelayStatus();
  const cfg = {
    connected: { dot: "bg-up", text: "text-up", label: "live", pulse: false },
    reconnecting: { dot: "bg-amber", text: "text-amber", label: "reconnecting", pulse: true },
    down: { dot: "bg-faint", text: "text-faint", label: "ws off", pulse: false },
  }[display];
  return (
    <span
      className={`flex items-center gap-1 text-[11px] ${cfg.text}`}
      title={`relay socket: ${socket} · upstream: ${upstream}`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? "pulse" : ""}`} />
      <span>{cfg.label}</span>
    </span>
  );
}

function AccountChip() {
  const { data, isLoading, isError } = useAuthMe();
  // Full sign-out: forget the SDK session (else it re-hydrates on reload) AND any manual key.
  const disconnect = omDisconnect;
  if (isLoading) return <span className="text-[11px] text-faint">auth…</span>;
  if (isError || !data) {
    // The connected key was rejected/expired — offer to disconnect back to the gate.
    return (
      <button
        onClick={disconnect}
        className="text-[11px] text-down underline hover:text-down/80"
        title="This key was rejected. Click to connect a different one."
      >
        auth ✗ — reconnect
      </button>
    );
  }
  const key = data.current_key;
  const credits = data.credits?.total ?? null;
  return (
    <span className="flex items-center gap-2 text-[11px]">
      <span className="text-ink">{key?.name ?? key?.key_prefix ?? "key"}</span>
      <button
        onClick={() => showCapabilities()}
        className="grid h-4 w-4 place-items-center rounded-full border border-border-2 text-[10px] text-dim hover:border-up hover:text-up"
        title="What can this key do?"
      >
        ⓘ
      </button>
      <span className="rounded border border-border-2 px-1 text-[10px] uppercase text-dim">
        {key?.key_type ?? "?"}
      </span>
      <span className={data.can_execute ? "text-up" : "text-faint"} title="can_execute">
        {data.can_execute ? "exec✓" : "read-only"}
      </span>
      {credits != null && (
        <span className="nums text-dim" title="credits remaining (auth/me credits.total)">
          {credits.toLocaleString()} cr
        </span>
      )}
      <button
        onClick={disconnect}
        className="text-faint hover:text-down"
        title="Sign out"
      >
        ⏻
      </button>
    </span>
  );
}
